import * as THREE from 'three';
import { LAYOUT } from '../config/gameConfig';
import {
  createMaterials,
  createPaintingTexture,
  type MaterialSet,
} from './materials';
import { makeInteractable, type InteractableData } from '../interaction/Interactable';
import type { AABB } from '../player/PlayerController';

export interface WorldBuildResult {
  group: THREE.Group;
  colliders: AABB[];
  interactables: Map<string, InteractableData>;
  materials: MaterialSet;
  refs: {
    clockHands: { hour: THREE.Object3D; minute: THREE.Object3D };
    drawer: THREE.Object3D;
    deskDrawer: THREE.Object3D;
    safeDoor: THREE.Object3D;
    basementDoor: THREE.Object3D;
    hiddenWall: THREE.Object3D;
    exitDoor: THREE.Object3D;
    fuseSlot: THREE.Mesh;
    mirrors: THREE.Object3D[];
    emblem: THREE.Mesh;
    powerLights: THREE.Mesh[];
    brazierFlame: THREE.Mesh;
    cabinetDoor: THREE.Object3D;
  };
}

function box(
  w: number,
  h: number,
  d: number,
  mat: THREE.Material,
  x: number,
  y: number,
  z: number,
  cast = true,
  receive = true,
): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
  m.position.set(x, y, z);
  m.castShadow = cast;
  m.receiveShadow = receive;
  return m;
}

function addCollider(colliders: AABB[], mesh: THREE.Object3D, pad = 0, tag?: string): void {
  const b = new THREE.Box3().setFromObject(mesh);
  colliders.push({
    minX: b.min.x - pad,
    maxX: b.max.x + pad,
    minZ: b.min.z - pad,
    maxZ: b.max.z + pad,
    minY: b.min.y,
    maxY: b.max.y,
    tag,
  });
}

export function buildWorld(): WorldBuildResult {
  const group = new THREE.Group();
  const colliders: AABB[] = [];
  const interactables = new Map<string, InteractableData>();
  const mats = createMaterials();
  const H = LAYOUT.ceilingY;
  const R = LAYOUT.rooms;

  const refs: WorldBuildResult['refs'] = {
    clockHands: { hour: new THREE.Object3D(), minute: new THREE.Object3D() },
    drawer: new THREE.Object3D(),
    deskDrawer: new THREE.Object3D(),
    safeDoor: new THREE.Object3D(),
    basementDoor: new THREE.Object3D(),
    hiddenWall: new THREE.Object3D(),
    exitDoor: new THREE.Object3D(),
    fuseSlot: box(0.15, 0.2, 0.08, mats.metal, 0, 0, 0),
    mirrors: [],
    emblem: box(0.5, 0.5, 0.05, mats.metalGold, 0, 0, 0),
    powerLights: [],
    brazierFlame: box(0.2, 0.25, 0.2, mats.emissiveWarm, 0, 0, 0, false),
    cabinetDoor: new THREE.Object3D(),
  };
  refs.brazierFlame.visible = false;

  function roomShell(
    cx: number,
    cz: number,
    w: number,
    d: number,
    wallMat: THREE.Material,
    floorMat: THREE.Material,
    openings: { dir: 'n' | 's' | 'e' | 'w'; width: number }[],
  ): void {
    const floor = box(w, 0.08, d, floorMat, cx, 0.04, cz, false, true);
    group.add(floor);
    const ceil = box(w, 0.08, d, mats.plaster, cx, H, cz, false, false);
    group.add(ceil);

    const t = LAYOUT.wallThickness;
    const halfW = w / 2;
    const halfD = d / 2;

    const walls: { dir: 'n' | 's' | 'e' | 'w'; width: number }[] = [
      { dir: 'n', width: openings.find((o) => o.dir === 'n')?.width ?? 0 },
      { dir: 's', width: openings.find((o) => o.dir === 's')?.width ?? 0 },
      { dir: 'e', width: openings.find((o) => o.dir === 'e')?.width ?? 0 },
      { dir: 'w', width: openings.find((o) => o.dir === 'w')?.width ?? 0 },
    ];

    for (const wall of walls) {
      const gap = wall.width;
      if (wall.dir === 'n' || wall.dir === 's') {
        const z = wall.dir === 'n' ? cz - halfD : cz + halfD;
        if (gap <= 0) {
          const m = box(w + t, H, t, wallMat, cx, H / 2, z);
          group.add(m);
          addCollider(colliders, m);
        } else {
          const side = (w - gap) / 2;
          const m1 = box(side, H, t, wallMat, cx - gap / 2 - side / 2, H / 2, z);
          const m2 = box(side, H, t, wallMat, cx + gap / 2 + side / 2, H / 2, z);
          const lintel = box(gap, H * 0.25, t, wallMat, cx, H * 0.875, z);
          group.add(m1, m2, lintel);
          addCollider(colliders, m1);
          addCollider(colliders, m2);
          addCollider(colliders, lintel);
        }
      } else {
        const x = wall.dir === 'w' ? cx - halfW : cx + halfW;
        if (gap <= 0) {
          const m = box(t, H, d + t, wallMat, x, H / 2, cz);
          group.add(m);
          addCollider(colliders, m);
        } else {
          const side = (d - gap) / 2;
          const m1 = box(t, H, side, wallMat, x, H / 2, cz - gap / 2 - side / 2);
          const m2 = box(t, H, side, wallMat, x, H / 2, cz + gap / 2 + side / 2);
          const lintel = box(t, H * 0.25, gap, wallMat, x, H * 0.875, cz);
          group.add(m1, m2, lintel);
          addCollider(colliders, m1);
          addCollider(colliders, m2);
          addCollider(colliders, lintel);
        }
      }
    }
  }

  // —— 部屋シェル ——
  roomShell(R.entrance.x, R.entrance.z, R.entrance.w, R.entrance.d, mats.wallpaper, mats.marble, [
    { dir: 'e', width: 2.6 },
    { dir: 's', width: 2.4 },
    { dir: 'w', width: 2.4 },
  ]);
  roomShell(R.living.x, R.living.z, R.living.w, R.living.d, mats.wallpaperWarm, mats.carpet, [
    { dir: 'w', width: 2.6 },
    { dir: 's', width: 2.4 },
    { dir: 'e', width: 2.4 },
  ]);
  roomShell(R.study.x, R.study.z, R.study.w, R.study.d, mats.wallpaper, mats.wood, [
    { dir: 'n', width: 2.4 },
    { dir: 'w', width: 2.4 },
  ]);
  roomShell(R.kitchen.x, R.kitchen.z, R.kitchen.w, R.kitchen.d, mats.plaster, mats.stone, [
    { dir: 'n', width: 2.4 },
    { dir: 'e', width: 2.4 },
  ]);
  roomShell(R.dining.x, R.dining.z, R.dining.w, R.dining.d, mats.wallpaperWarm, mats.wood, [
    { dir: 'w', width: 2.4 },
  ]);
  roomShell(R.basement.x, R.basement.z, R.basement.w, R.basement.d, mats.stone, mats.stone, [
    { dir: 'e', width: 2.4 },
    { dir: 's', width: 2.4 },
  ]);
  roomShell(R.hidden.x, R.hidden.z, R.hidden.w, R.hidden.d, mats.wallpaper, mats.marble, [
    { dir: 'n', width: 2.4 },
  ]);

  // —— エントランス ——
  {
    const cx = R.entrance.x;
    const cz = R.entrance.z;
    const halfD = R.entrance.d / 2;
    const halfW = R.entrance.w / 2;

    const door = box(1.6, 2.4, 0.12, mats.door, cx, 1.2, cz - halfD + 0.12);
    group.add(door);
    addCollider(colliders, door, 0, 'exit_door');
    refs.exitDoor = door;
    interactables.set(
      'exit_door',
      makeInteractable(door, {
        id: 'exit_door',
        kind: 'exit',
        title: '玄関の大扉',
        body: '重厚な扉は固く閉ざされている。鍵穴とテンキーがある。',
        prompt: 'E 調べる',
      }),
    );

    const paintMat = mats.painting.clone();
    paintMat.map = createPaintingTexture();
    paintMat.needsUpdate = true;
    const painting = box(1.6, 1.1, 0.06, paintMat, cx - 2.2, 1.8, cz - halfD + 0.18);
    const frame = box(1.75, 1.25, 0.08, mats.metalGold, cx - 2.2, 1.8, cz - halfD + 0.14);
    group.add(painting, frame);
    interactables.set(
      'painting',
      makeInteractable(painting, {
        id: 'painting',
        kind: 'examine',
        title: '夕暮れの絵画',
        body:
          '荒れた空の下に立つ館が描かれている。\n\n上部にローマ数字で「III : XV」と記されている。\n右下の隅に、ほとんど消えた数字「48」が見える。\n\nフレーム裏を触ると、金庫の数字と時計が関係している予感がする。',
        prompt: 'E 観察する',
      }),
    );

    const table = box(1.4, 0.08, 0.45, mats.wood, cx + 2.2, 0.75, cz - 1.5);
    group.add(table, box(0.08, 0.75, 0.08, mats.wood, cx + 1.6, 0.375, cz - 1.5), box(0.08, 0.75, 0.08, mats.wood, cx + 2.8, 0.375, cz - 1.5));
    addCollider(colliders, table);
    const note = box(0.25, 0.02, 0.18, mats.fabric, cx + 2.2, 0.8, cz - 1.5);
    (note.material as THREE.MeshStandardMaterial).color.set(0xf0e8d0);
    group.add(note);
    interactables.set(
      'intro_note',
      makeInteractable(note, {
        id: 'intro_note',
        kind: 'examine',
        title: '置き手紙',
        body:
          '「ようこそ、夜の館へ。\n真実は《時間》と《言葉》と《旋律》と《光》の中にある。\n出口は、すべてを繋いだ者にのみ開く。」\n\n端に小さく: Dining — Etude VII',
        prompt: 'E 読む',
      }),
    );

    // コートラック（フレーバー）
    const rack = box(0.15, 1.6, 0.15, mats.woodDark, cx - 3, 0.8, cz + 2);
    group.add(rack);
    addCollider(colliders, rack);
    interactables.set(
      'coatrack',
      makeInteractable(rack, {
        id: 'coatrack',
        kind: 'examine',
        title: 'コートラック',
        body: '誰かの外套が掛かっている。ポケットは空だ。\n襟元のタグに「客用・ダイニング側」とある。',
        prompt: 'E 調べる',
      }),
    );

    group.add(box(0.6, 0.15, 0.6, mats.metalGold, cx, 2.9, cz, false));
    group.add(box(0.2, 0.2, 0.2, mats.emissiveWarm, cx, 2.75, cz, false));
    const col1 = box(0.25, H, 0.25, mats.marble, cx - halfW + 0.8, H / 2, cz - halfD + 0.8);
    const col2 = box(0.25, H, 0.25, mats.marble, cx + halfW - 0.8, H / 2, cz - halfD + 0.8);
    group.add(col1, col2);
    addCollider(colliders, col1);
    addCollider(colliders, col2);
  }

  // —— リビング ——
  {
    const cx = R.living.x;
    const cz = R.living.z;

    const sofa = box(2.4, 0.55, 0.9, mats.fabric, cx - 1, 0.35, cz + 2.8);
    group.add(sofa, box(2.4, 0.7, 0.2, mats.fabric, cx - 1, 0.8, cz + 3.25));
    addCollider(colliders, sofa);
    const ltable = box(1.2, 0.08, 0.7, mats.wood, cx - 1, 0.4, cz + 1.4);
    group.add(ltable);
    addCollider(colliders, ltable);

    // 暖炉
    const fireX = cx + 1.5;
    const fireZ = cz - 3.8;
    const fire = box(1.6, 1.5, 0.55, mats.stone, fireX, 0.75, fireZ);
    const fireIn = box(1.0, 0.7, 0.2, mats.emissiveWarm, fireX, 0.55, fireZ + 0.2);
    group.add(fire, fireIn);
    addCollider(colliders, fire);
    interactables.set(
      'fireplace',
      makeInteractable(fire, {
        id: 'fireplace',
        kind: 'fireplace',
        title: '暖炉',
        body: '炎が静かに揺れている。熱に弱い紙なら、ここで炙れるかもしれない。',
        prompt: 'E 調べる / 使う',
      }),
    );

    // 置時計
    const clockX = cx - 3.2;
    const clockZ = cz - 3.2;
    const clockBody = box(0.35, 0.9, 0.2, mats.woodDark, clockX, 1.3, clockZ);
    const clockFace = new THREE.Mesh(
      new THREE.CircleGeometry(0.22, 32),
      new THREE.MeshStandardMaterial({ color: 0xf0e8d8, roughness: 0.4 }),
    );
    clockFace.position.set(clockX, 1.55, clockZ + 0.12);
    const hourPivot = new THREE.Group();
    const minutePivot = new THREE.Group();
    hourPivot.position.set(clockX, 1.55, clockZ + 0.14);
    minutePivot.position.set(clockX, 1.55, clockZ + 0.15);
    hourPivot.add(box(0.03, 0.1, 0.02, mats.metal, 0, 0.05, 0));
    minutePivot.add(box(0.02, 0.14, 0.02, mats.metal, 0, 0.07, 0));
    group.add(clockBody, clockFace, hourPivot, minutePivot);
    addCollider(colliders, clockBody);
    refs.clockHands.hour = hourPivot;
    refs.clockHands.minute = minutePivot;
    interactables.set(
      'clock',
      makeInteractable(clockBody, {
        id: 'clock',
        kind: 'clock',
        title: '置時計',
        body: '針を動かして時刻を合わせられる。',
        prompt: 'E 時刻を合わせる',
      }),
    );

    const sbX = cx - 3.2;
    const sbZ = cz + 3.5;
    const sideboard = box(1.5, 0.7, 0.5, mats.wood, sbX, 0.4, sbZ);
    const drawer = box(1.3, 0.22, 0.45, mats.woodDark, sbX, 0.35, sbZ);
    group.add(sideboard, drawer);
    addCollider(colliders, sideboard);
    refs.drawer = drawer;
    interactables.set(
      'living_drawer',
      makeInteractable(drawer, {
        id: 'living_drawer',
        kind: 'drawer',
        title: 'サイドボードの引き出し',
        body: '奥の機構がロックされているようだ。時計と連動している？',
        prompt: 'E 開ける',
        locked: true,
      }),
    );

    // ピアノ
    const px = cx + 3.2;
    const pz = cz + 1.5;
    const piano = box(1.8, 0.85, 0.7, mats.woodDark, px, 0.5, pz);
    const pianoLid = box(1.7, 0.06, 0.55, mats.woodDark, px, 0.95, pz);
    group.add(piano, pianoLid);
    addCollider(colliders, piano);
    // 鍵盤風
    for (let i = 0; i < 7; i++) {
      group.add(
        box(
          0.18,
          0.04,
          0.35,
          new THREE.MeshStandardMaterial({ color: i % 2 === 0 ? 0xf5f0e8 : 0x222222, roughness: 0.5 }),
          px - 0.55 + i * 0.2,
          0.95,
          pz + 0.05,
        ),
      );
    }
    interactables.set(
      'piano',
      makeInteractable(piano, {
        id: 'piano',
        kind: 'piano',
        title: 'グランドピアノ（小型）',
        body: '埃を被った鍵盤。楽譜があれば旋律が分かるだろう。',
        prompt: 'E 演奏する',
      }),
    );

    group.add(box(0.15, 1.4, 0.15, mats.metal, cx + 3.5, 0.7, cz - 1.5));
    group.add(box(0.4, 0.25, 0.4, mats.emissiveWarm, cx + 3.5, 1.5, cz - 1.5, false));
  }

  // —— ダイニング ——
  {
    const cx = R.dining.x;
    const cz = R.dining.z;

    const dtable = box(2.4, 0.08, 1.2, mats.wood, cx, 0.78, cz);
    const dleg = box(2.2, 0.7, 1.0, mats.woodDark, cx, 0.35, cz);
    group.add(dtable, dleg);
    addCollider(colliders, dleg);

    // 椅子×2
    for (const oz of [-1.1, 1.1]) {
      const ch = box(0.45, 0.5, 0.45, mats.fabric, cx, 0.35, cz + oz);
      group.add(ch);
      addCollider(colliders, ch);
    }

    const sheet = box(0.35, 0.02, 0.28, mats.fabric, cx + 0.3, 0.84, cz);
    (sheet.material as THREE.MeshStandardMaterial).color.set(0xf8f4e8);
    group.add(sheet);
    interactables.set(
      'sheet_music',
      makeInteractable(sheet, {
        id: 'sheet_music',
        kind: 'sheet_music',
        title: '楽譜 — Etude VII',
        body:
          'タイトル: Etude VII\n\n簡略記号で記された旋律:\n  ♩ C   ♩ E   ♩ G\n（ド・ミ・ソ）\n\n余白に手書き:\n「圧力は題号の数」',
        prompt: 'E 読む',
      }),
    );

    const candelabra = box(0.15, 0.4, 0.15, mats.metalGold, cx - 0.5, 1.0, cz);
    group.add(candelabra);
    group.add(box(0.08, 0.12, 0.08, mats.emissiveWarm, cx - 0.5, 1.25, cz, false));

    const side = box(1.2, 1.1, 0.45, mats.wood, cx + 2.5, 0.55, cz - 2);
    group.add(side);
    addCollider(colliders, side);
    interactables.set(
      'dining_sideboard',
      makeInteractable(side, {
        id: 'dining_sideboard',
        kind: 'examine',
        title: '食器棚',
        body: '銀器が並ぶ。引き出しは空。\n裏板に「VII = 7」と鉛筆で書かれている。',
        prompt: 'E 調べる',
      }),
    );

    // 窓枠（装飾）
    const win = box(1.4, 1.2, 0.08, mats.woodDark, cx, 1.6, cz - R.dining.d / 2 + 0.15);
    group.add(win);
  }

  // —— 書斎 ——
  {
    const cx = R.study.x;
    const cz = R.study.z;

    const desk = box(1.6, 0.08, 0.8, mats.wood, cx, 0.75, cz + 0.5);
    const deskLeg = box(1.5, 0.7, 0.7, mats.woodDark, cx, 0.35, cz + 0.5);
    const deskDrawer = box(0.5, 0.18, 0.6, mats.wood, cx + 0.4, 0.55, cz + 0.5);
    group.add(desk, deskLeg, deskDrawer);
    addCollider(colliders, deskLeg);
    refs.deskDrawer = deskDrawer;
    interactables.set(
      'study_desk',
      makeInteractable(deskDrawer, {
        id: 'study_desk',
        kind: 'use_item',
        title: '書斎の机',
        body: '引き出しに鍵穴がある。',
        prompt: 'E / F 鍵を使う',
        requires: 'copper_key',
        locked: true,
      }),
    );

    const shelfX = cx + 3.2;
    const shelfZ = cz + 2.5;
    const shelf = box(2.2, 2.2, 0.4, mats.woodDark, shelfX, 1.1, shelfZ);
    group.add(shelf);
    addCollider(colliders, shelf);
    const colors = [0xaa3030, 0x3050aa, 0x40aa50, 0xccaa30, 0x8040a0, 0x4080a0];
    for (let i = 0; i < 12; i++) {
      group.add(
        box(
          0.12,
          0.35 + (i % 3) * 0.05,
          0.28,
          new THREE.MeshStandardMaterial({ color: colors[i % colors.length], roughness: 0.8 }),
          shelfX - 0.9 + (i % 6) * 0.28,
          0.9 + Math.floor(i / 6) * 0.7,
          shelfZ + 0.15,
        ),
      );
    }
    interactables.set(
      'bookshelf',
      makeInteractable(shelf, {
        id: 'bookshelf',
        kind: 'bookshelf',
        title: '本棚',
        body: '特に目立つ四冊の背表紙が、色で区別できる。並べ替えられそうだ。',
        prompt: 'E 並べ替える',
      }),
    );

    const safe = box(0.6, 0.6, 0.5, mats.metal, cx - 3, 0.5, cz + 2.5);
    const safeDoor = box(0.55, 0.55, 0.06, mats.metal, cx - 3, 0.5, cz + 2.22);
    group.add(safe, safeDoor);
    addCollider(colliders, safe);
    refs.safeDoor = safeDoor;
    interactables.set(
      'safe',
      makeInteractable(safe, {
        id: 'safe',
        kind: 'safe',
        title: '金庫',
        body: '4桁のパスコード。ただし《言葉》の儀式を終えていないと反応しない。',
        prompt: 'E パスコードを入力',
        locked: true,
      }),
    );

    const chair = box(0.45, 0.5, 0.45, mats.fabric, cx, 0.35, cz - 0.5);
    group.add(chair);
    addCollider(colliders, chair);

    // 地球儀（フレーバー）
    const globe = box(0.25, 0.35, 0.25, mats.metal, cx - 2, 1.0, cz - 0.5);
    group.add(globe);
    interactables.set(
      'globe',
      makeInteractable(globe, {
        id: 'globe',
        kind: 'examine',
        title: '地球儀',
        body: '古い地球儀。ピンは刺さっていない。\n台座に「先に《並び》を整えよ」と刻まれている。',
        prompt: 'E 調べる',
      }),
    );
  }

  // —— キッチン ——
  {
    const cx = R.kitchen.x;
    const cz = R.kitchen.z;

    const counter = box(3.5, 0.9, 0.7, mats.stone, cx - 0.5, 0.45, cz + 2.5);
    group.add(counter);
    addCollider(colliders, counter);

    const valveDefs = [
      { color: 0x3080cc, x: cx - 1.5 },
      { color: 0xccaa30, x: cx - 0.5 },
      { color: 0xcc4040, x: cx + 0.5 },
    ];
    for (const v of valveDefs) {
      group.add(
        box(
          0.2,
          0.25,
          0.2,
          new THREE.MeshStandardMaterial({ color: v.color, metalness: 0.6, roughness: 0.3 }),
          v.x,
          1.0,
          cz + 2.2,
        ),
      );
    }
    interactables.set(
      'valves',
      makeInteractable(counter, {
        id: 'valves',
        kind: 'valves',
        title: 'キッチンのバルブ',
        body: '水・油・ガスの三本。順序を間違えるとリセットされる。',
        prompt: 'E 操作する',
      }),
    );

    const fridge = box(0.8, 1.8, 0.7, mats.metal, cx + 2.5, 0.9, cz + 2.5);
    group.add(fridge);
    addCollider(colliders, fridge);
    interactables.set(
      'fridge',
      makeInteractable(fridge, {
        id: 'fridge',
        kind: 'examine',
        title: '冷蔵庫',
        body:
          '磁石メモ:\n\n「今夜の段取り\n① 水を通せ\n② 油を回せ\n③ 最後にガス」\n\n端: 青→黄→赤\n\n※戸棚の中も確認すること',
        prompt: 'E 開ける',
      }),
    );

    // 戸棚（銀鍵）
    const cab = box(1.0, 1.4, 0.45, mats.wood, cx - 2.8, 0.9, cz - 1.5);
    const cabDoor = box(0.9, 1.2, 0.08, mats.woodDark, cx - 2.8, 0.9, cz - 1.25);
    group.add(cab, cabDoor);
    addCollider(colliders, cab);
    refs.cabinetDoor = cabDoor;
    interactables.set(
      'cabinet',
      makeInteractable(cab, {
        id: 'cabinet',
        kind: 'cabinet',
        title: 'キッチンの戸棚',
        body: '細い鍵穴がある戸棚。',
        prompt: 'E / F 鍵を使う',
        requires: 'silver_key',
        locked: true,
      }),
    );

    group.add(box(0.6, 0.15, 0.45, mats.metal, cx + 0.5, 0.95, cz + 2.4));
    const ktable = box(1.2, 0.7, 0.8, mats.wood, cx, 0.4, cz - 1);
    group.add(ktable);
    addCollider(colliders, ktable);
  }

  // —— 地下室 ——
  {
    const cx = R.basement.x;
    const cz = R.basement.z;
    const halfW = R.basement.w / 2;
    const halfD = R.basement.d / 2;

    const bdoor = box(0.12, 2.2, 1.5, mats.door, R.entrance.x - R.entrance.w / 2 + 0.08, 1.1, R.entrance.z);
    group.add(bdoor);
    addCollider(colliders, bdoor, 0, 'basement_door');
    refs.basementDoor = bdoor;
    interactables.set(
      'basement_door',
      makeInteractable(bdoor, {
        id: 'basement_door',
        kind: 'door',
        title: '地下室への扉',
        body: '鉄の鍵穴がある重い扉。',
        prompt: 'E / F 鍵を使う',
        requires: 'basement_key',
        locked: true,
      }),
    );

    const panel = box(0.2, 1.2, 0.8, mats.metal, cx - halfW + 0.5, 1.4, cz - 1.5);
    group.add(panel);
    addCollider(colliders, panel);
    refs.fuseSlot.position.set(cx - halfW + 0.65, 1.7, cz - 1.5);
    refs.fuseSlot.material = mats.metal.clone();
    (refs.fuseSlot.material as THREE.MeshStandardMaterial).color.set(0x333333);
    group.add(refs.fuseSlot);
    interactables.set(
      'fusebox',
      makeInteractable(panel, {
        id: 'fusebox',
        kind: 'fusebox',
        title: '配電盤',
        body: 'ヒューズと配線端子。圧力が適正でないと通電しない。',
        prompt: 'E 操作する',
      }),
    );

    // 圧力計
    const gauge = box(0.35, 0.35, 0.15, mats.metal, cx + 2.5, 1.4, cz - 2.5);
    const gaugeFace = new THREE.Mesh(
      new THREE.CircleGeometry(0.14, 24),
      new THREE.MeshStandardMaterial({ color: 0xe8e0d0, roughness: 0.35 }),
    );
    gaugeFace.position.set(cx + 2.5, 1.4, cz - 2.4);
    group.add(gauge, gaugeFace);
    addCollider(colliders, gauge);
    interactables.set(
      'pressure',
      makeInteractable(gauge, {
        id: 'pressure',
        kind: 'pressure',
        title: '圧力計',
        body: '配管の圧力を調整するダイヤル。0〜12。',
        prompt: 'E 調整する',
      }),
    );

    const crate = box(0.8, 0.6, 0.8, mats.wood, cx + 2, 0.3, cz + 1.5);
    group.add(crate);
    addCollider(colliders, crate);
    interactables.set(
      'crate',
      makeInteractable(crate, {
        id: 'crate',
        kind: 'examine',
        title: '古い木箱',
        body: '内側に「光は鏡に導かれる」と彫ってある。\n側面の刻印: 90° / 180° / 0°\n底に「圧力を先に整えよ」',
        prompt: 'E 調べる',
      }),
    );

    const hwall = box(2.0, 2.5, 0.3, mats.stone, cx, 1.25, cz + halfD - 0.05);
    group.add(hwall);
    addCollider(colliders, hwall, 0, 'hidden_wall');
    refs.hiddenWall = hwall;

    group.add(box(0.3, 0.15, 0.3, mats.emissiveCool, cx, 2.8, cz, false));
  }

  // —— 隠し部屋 ——
  {
    const cx = R.hidden.x;
    const cz = R.hidden.z;
    const halfD = R.hidden.d / 2;

    for (let i = 0; i < 3; i++) {
      const mx = cx - 2 + i * 2;
      const mz = cz + halfD - 0.4;
      const frame = box(0.7, 1.2, 0.1, mats.metalGold, mx, 1.4, mz);
      group.add(frame, box(0.55, 1.0, 0.05, mats.glass, mx, 1.4, mz + 0.08));
      refs.mirrors.push(frame);
    }
    interactables.set(
      'mirrors',
      makeInteractable(refs.mirrors[1], {
        id: 'mirrors',
        kind: 'mirrors',
        title: '三面鏡',
        body: '三つの鏡。火床に光が灯っていなければ意味をなさない。',
        prompt: 'E 鏡を調整',
      }),
    );

    refs.emblem.position.set(cx + 3.5, 1.6, cz + halfD - 0.4);
    refs.emblem.material = mats.metalGold.clone();
    group.add(refs.emblem);
    interactables.set(
      'emblem',
      makeInteractable(refs.emblem, {
        id: 'emblem',
        kind: 'examine',
        title: '壁の紋章',
        body: 'まだ光が当たっていない。',
        prompt: 'E 見る',
      }),
    );

    // 火床 + ランタン用
    const brazier = box(0.6, 0.5, 0.6, mats.stone, cx - 3.2, 0.35, cz);
    refs.brazierFlame.position.set(cx - 3.2, 0.7, cz);
    group.add(brazier, refs.brazierFlame);
    addCollider(colliders, brazier);
    interactables.set(
      'brazier',
      makeInteractable(brazier, {
        id: 'brazier',
        kind: 'brazier',
        title: '石の火床',
        body: '冷たい灰だけが残る。油の灯りを置けそうだ。',
        prompt: 'E / F ランタンを使う',
      }),
    );

    const pedestal = box(0.5, 0.8, 0.5, mats.marble, cx, 0.4, cz + 1);
    group.add(pedestal);
    addCollider(colliders, pedestal);

    group.add(box(0.3, 0.2, 0.3, mats.emissiveWarm, cx - 3.5, 2.6, cz, false));
  }

  // 接続床
  group.add(
    box(0.5, 0.05, 2.4, mats.marble, 4, 0.03, 0, false, true),
    box(2.4, 0.05, 0.5, mats.carpet, 9, 0.03, 5, false, true),
    box(0.5, 0.05, 2.4, mats.stone, 4, 0.03, 8, false, true),
    box(2.4, 0.05, 0.5, mats.plaster, 0, 0.03, 4, false, true),
    box(0.5, 0.05, 2.4, mats.stone, -4, 0.03, 0, false, true),
    box(2.4, 0.05, 0.5, mats.stone, -9, 0.03, 4, false, true),
    box(0.5, 0.05, 2.4, mats.wood, 14, 0.03, 0, false, true), // living-dining
  );

  return { group, colliders, interactables, materials: mats, refs };
}

export function updateClockHands(
  hourHand: THREE.Object3D,
  minuteHand: THREE.Object3D,
  hour: number,
  minute: number,
): void {
  const minuteAngle = -(minute / 60) * Math.PI * 2;
  const hourAngle = -((hour % 12) / 12) * Math.PI * 2 - (minute / 60) * ((Math.PI * 2) / 12);
  minuteHand.rotation.z = minuteAngle;
  hourHand.rotation.z = hourAngle;
}
